import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "80";
const height = "40";
const viewBox = "0 0 80 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            d="M27.956 25.106c0-.256.15-.916.278-1.384l1.876-7.012c.042-.192-.086-.298-.32-.298h-1.62a.513.513 0 00-.448.212l-.832.916c-.788-.788-1.982-1.342-3.496-1.342-2.622 0-5.52 1.812-6.35 5.478-.938 4.114 1.172 6.948 4.796 6.948 1.386 0 2.558-.512 3.368-1.342.532.832 1.3 1.236 2.728 1.236 1.428 0 2.26-.596 2.472-1.514.256-1.044-.234-1.642-1.086-1.642-.426 0-.682.192-.938.192-.278 0-.428-.172-.428-.448zM7.454 15.004c1.3 0 2.472.766 3.942.766.214 0 .426-.022.618-.086L.166 26.172c-.214.17-.214.426-.042.618l1.662 1.79c.192.212.404.192.746-.022.596-.384 1.278-.852 2.302-.852 1.748 0 3.794.938 6.138.938 1.598.022 2.834-.596 3.452-1.47.64-.916.724-1.748.724-2.472 0-1.044-.64-1.834-1.704-1.834-.874 0-1.406.554-1.47 1.236-.042.362.022.874-.214 1.214-.214.32-.532.448-.98.448-1.236 0-2.472-.724-4.284-.724-.32 0-.64.064-.938.128L17.28 14.834c.192-.17.192-.32.022-.512l-1.62-1.662c-.15-.17-.298-.17-.468-.022-.448.384-1.214.64-2.216.64-1.896 0-3.708-1.002-5.818-1.002-1.534 0-2.6.448-3.41 1.236-.746.746-1.066 1.854-1.066 2.664 0 .958.596 1.768 1.662 1.768.916 0 1.534-.576 1.534-1.364 0-.468.106-.916.342-1.172.254-.276.636-.404 1.212-.404zm66.578 13.618c1.96 0 3.474-.64 4.114-1.28.406-.404.64-.874.64-1.406 0-.66-.468-1.15-1.322-1.15-1.172 0-1.62 1.15-3.41 1.15-1.896 0-3.26-1.194-3.304-3.026 1.172.554 2.664.938 4.432.938 2.196 0 4.114-.788 4.646-2.6.768-2.686-1.086-5.05-5.03-5.05-3.388 0-5.988 1.918-6.842 4.816-1.17 4.006 1.196 7.608 6.076 7.608zm-26.894-.234c.128.17.404.15.64-.042.512-.406 1.002-.724 1.94-.724 1.768 0 3.26.938 5.37.938 1.62.022 2.622-.576 3.196-1.406.448-.64.66-1.512.66-2.194 0-.958-.554-1.598-1.512-1.598-.916 0-1.322.468-1.364 1.15-.022.278-.022.81-.192 1.066-.17.278-.468.342-.852.342-1.002 0-2.28-.64-3.602-.64h-.15l8.696-6.948c.15-.106.214-.362.042-.49l-1.322-1.556c-.234-.214-.406-.192-.64 0-.448.362-.874.724-1.79.724-1.492 0-3.026-.894-5.116-.894-1.492 0-2.28.512-2.856 1.108-.554.596-.938 1.556-.938 2.472 0 .938.554 1.534 1.47 1.534.832 0 1.322-.468 1.322-1.13 0-.384.022-.724.192-.98.15-.256.49-.448 1.024-.448.832 0 2.344.532 3.324.532h.064L45.9 26.11c-.17.128-.17.426-.064.576l1.302 1.702zm-9.89-3.112c-.128 0-.234 0-.362.022l8.652-6.948c.15-.106.212-.362.042-.49l-1.322-1.576c-.234-.214-.406-.192-.64 0-.448.362-.874.724-1.79.724-1.492 0-3.112-.894-5.2-.894-1.492 0-2.28.512-2.856 1.108-.554.596-.896 1.556-.896 2.472 0 .938.554 1.534 1.47 1.534.788 0 1.322-.468 1.322-1.13 0-.384.064-.724.234-.98.15-.256.49-.448 1.022-.448.832 0 2.344.532 3.324.532h.128L31.66 26c-.17.15-.17.426-.064.576l1.386 1.812c.128.17.404.15.64-.042.512-.406.98-.724 1.918-.724 1.662 0 3.09.938 5.2.938 1.62.022 2.622-.576 3.196-1.406.448-.64.66-1.512.66-2.194 0-.958-.554-1.598-1.514-1.598-.852 0-1.322.468-1.364 1.15-.022.278-.022.81-.192 1.066-.17.278-.468.342-.852.342-.996-.004-2.082-.644-3.426-.644zm30.264-12.978c.042-.192-.064-.298-.276-.298h-2.43c-.256 0-.362.128-.426.362l-3.09 10.592c-.404 1.386-.532 2.088-.532 2.92 0 .66.256 1.342.66 1.768.468.49 1.258.874 2.558.874 1.364 0 2.28-.49 2.558-1.45.298-1.002-.298-1.662-1.086-1.662-.618 0-.788.214-1.108.214-.234 0-.448-.17-.448-.47 0-.34.17-1.066.32-1.576l3.3-11.274zM22.734 25.83c-1.812 0-2.962-1.364-2.43-3.814.448-2.088 1.94-3.26 3.496-3.26.98 0 1.684.426 2.132.98L24.59 25c-.448.49-1.046.83-1.856.83zm51.982-7.182c1.534 0 2.386.832 2.132 1.684-.192.704-.874 1.002-2.366 1.002-1.194 0-2.344-.298-3.24-.64.638-1.258 1.832-2.046 3.474-2.046z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Zazzle"
});
